// =============================================================================
// CU launcher semantic color palette - WIRED (runtime)
// =============================================================================
// Immutable, self-contained copy of the CU launcher interface colors for the
// Site Builder UI chrome only (NOT customer website colors). WIRED: the
// runtime theme (src/theme/antdTheme.js) spreads these tokens, seeds and
// component overrides into ConfigProvider, and
// src/assets/styles/abstracts/_variables.scss maps its UI semantics onto the
// Sass twin below.
//
// Provenance - read-only host reference, values copied verbatim (the app never
// imports from the host repository at runtime):
// centrivo-universal-backoffice-client
//   packages/launcher/src/Providers/index.js (lines 9 and 36) mounts UIProvider
//   packages/launcher/src/Providers/UIProvider/index.js (lines 80-118) spreads
//     ANT_PROVIDER_PROPS into ConfigProvider via responsiveAntThemeProps
//   packages/launcher/src/shared/themes/antd-config-provider.js (lines 50-59)
//     spreads COLORS into theme.token; the host runs antd ^6.6.0
//     (packages/launcher/package.json line 58, packages/shared/package.json
//     line 55) - the same major as this app's antd 6.6.2
//   packages/shared/src/shared/themes/colors.js (lines 1-115) declares
//     BRAND_COLORS, NEUTRAL_COLORS and COLORS
//   packages/launcher/src/shared/themes/theme.js mirrors the exact same COLORS
//     into the styled-components theme ({ ...COLORS, neutral: NEUTRAL_COLORS })
//
// Verified scope decisions:
// - Fixed LIGHT theme: the chain contains no dark algorithm and no brand
//   selector, so there are no dark or brand variants to capture.
// - UIProvider responsive scaling (scaleThemeTokens/compactAlgorithm) is
//   numeric only; colors are never scaled.
// - Geometry (typography, radii, control heights, spacing, shadows, z-index)
//   is out of scope and deliberately not mirrored.
// - Global colorBorderSecondary is NOT declared by the host (AntD derives the
//   light default #F0F0F0), so it is deliberately absent from the tokens; only
//   Card and Tabs declare component-scoped values (see the overrides below).
// - Status colors are NOT launcher COLORS: the host inherits the AntD seeds,
//   captured separately in CU_ANTD_SEED_STATUS without derived values.
// - PIE_CHART_COLORS and ROW_COLORS (host colors.js lines 117-136) are data
//   visualization colors, not interface tokens, and are excluded.
//
// Sass twin (wired through _variables.scss): src/assets/styles/abstracts/_cu-palette.scss
// Strict JS/Sass parity is enforced by
// src/theme/__tests__/cuPalette.test.js.
// =============================================================================

/** Host: packages/shared/src/shared/themes/colors.js - BRAND_COLORS (lines 1-12). */
export const CU_BRAND_RAMP = Object.freeze({
	Color1: "#E6F6FF",
	Color2: "#A3DCFF",
	Color3: "#7AC8FF",
	Color4: "#52B1FF",
	Color5: "#2997FF",
	Color6: "#007DFF",
	Color7: "#0062D9",
	Color8: "#004AB3",
	Color9: "#00368C",
	Color10: "#002466",
});

/** Host: packages/shared/src/shared/themes/colors.js - NEUTRAL_COLORS (lines 14-29). */
export const CU_NEUTRAL_RAMP = Object.freeze({
	Color0: "#FFFFFF",
	Color1: "#FFFFFF",
	Color2: "#F7F8F9",
	Color3: "#EEF0F2",
	Color4: "#E5E7EA",
	Color5: "#C2C6CB",
	Color6: "#9FA4AC",
	Color7: "#8D939C",
	Color8: "#47505E",
	Color9: "#353F4F",
	Color10: "#303947",
	Color11: "#2A323F",
	Color12: "#242B36",
	Color13: "#000000",
});

/**
 * Host: packages/shared/src/shared/themes/colors.js - COLORS (lines 31-115),
 * spread verbatim into theme.token by
 * packages/launcher/src/shared/themes/antd-config-provider.js (lines 50-59).
 * AntD token names are kept exactly: src/theme/antdTheme.js spreads this
 * object into ConfigProvider theme.token with no key mapping. Ramp
 * references are resolved and cited per token (B# = BRAND_COLORS,
 * N# = NEUTRAL_COLORS).
 */
export const CU_LAUNCHER_TOKENS = Object.freeze({
	// primary colors (host COLORS lines 32-42)
	colorPrimary: "#007DFF", // B6
	colorPrimaryBg: "#E6F6FF", // B1
	colorPrimaryBgHover: "#E6F6FF", // B1
	colorPrimaryBorder: "#7AC8FF", // B3
	colorPrimaryBorderHover: "#52B1FF", // B4
	colorPrimaryHover: "#2997FF", // B5
	colorPrimaryActive: "#0062D9", // B7
	colorPrimaryTextHover: "#2997FF", // B5
	colorPrimaryText: "#007DFF", // B6
	colorPrimaryTextActive: "#0062D9", // B7

	// secondary colors (host COLORS lines 44-48)
	magenta: "#871b75", // literal in host COLORS ("magenta our secondary colors")

	// text colors (host COLORS lines 49-58)
	colorText: "#47505E", // N8
	colorTextSecondary: "#8D939C", // N7
	colorTextTertiary: "#9FA4AC", // N6
	colorTextQuaternary: "#C2C6CB", // N5
	colorTextHeading: "#47505E", // N8
	colorTextDescription: "#9FA4AC", // N6
	colorTextPlaceholder: "#9FA4AC", // N6
	colorTextDisabled: "#C2C6CB", // N5 - global disabled text
	colorTextLightSolid: "#FFFFFF", // N1

	// icon colors (host COLORS lines 60-62)
	colorIcon: "#8D939C", // N7
	colorIconHover: "#2997FF", // B5

	// background colors (host COLORS lines 64-76)
	colorBgContainer: "#FFFFFF", // N1
	colorBgElevated: "#FFFFFF", // N1
	colorBgLayout: "#E5E7EA", // N4
	colorBgMask: "#00000073", // literal (rgba(0, 0, 0, 0.45))
	colorBgSpotlight: "#000000D9", // literal (rgba(0, 0, 0, 0.85))
	colorBgContainerDisabled: "#F7F8F9", // N2
	colorBgTextActive: "#C2C6CB", // N5
	colorBgTextHover: "#EEF0F2", // N3
	colorBorderBg: "#FFFFFF", // N1
	colorBgSolid: "#000000", // literal
	colorBgSolidActive: "#000000D9", // literal
	colorBgSolidHover: "#000000BF", // literal

	// border colors (host COLORS lines 78-80)
	colorBorder: "#D9D9D9", // literal
	colorSplit: "#0000000F", // literal

	// fill colors (host COLORS lines 82-89)
	colorFill: "#00000026", // literal
	colorFillSecondary: "#0000000F", // literal
	colorFillTertiary: "#0000000A", // literal
	colorFillQuaternary: "#00000005", // literal
	colorFillContent: "#EEF0F2", // N3
	colorFillContentHover: "#C2C6CB", // N5
	colorFillAlter: "#0000000A", // literal

	// info colors (host COLORS lines 91-101)
	colorInfo: "#007DFF", // B6
	colorInfoBg: "#E6F6FF", // B1
	colorInfoBgHover: "#A3DCFF", // B2 - source-exact exception, NOT E6F6FF
	colorInfoBorder: "#7AC8FF", // B3
	colorInfoBorderHover: "#52B1FF", // B4
	colorInfoHover: "#2997FF", // B5
	colorInfoActive: "#0062D9", // B7
	colorInfoTextHover: "#47505E", // N8 - source-exact exception, not blue
	colorInfoText: "#007DFF", // B6
	colorInfoTextActive: "#0062D9", // B7

	// link colors (host COLORS lines 103-106)
	colorLink: "#007DFF", // B6
	colorLinkHover: "#2997FF", // B5
	colorLinkActive: "#0062D9", // B7

	// control colors (host COLORS lines 108-114)
	controlItemBgActive: "#E6F6FF", // B1
	controlItemBgActiveDisabled: "#00000026", // literal
	controlItemBgActiveHover: "#E6F6FF", // B1
	controlItemBgHover: "#0000000A", // literal
	controlOutline: "#398FCE1A", // literal
	controlTmpOutline: "#00000005", // literal
});

/**
 * Host: packages/launcher/src/shared/themes/antd-config-provider.js -
 * ANT_PROVIDER_PROPS.theme.components (lines 60-147), color tokens only.
 * Geometry, shadow and z-index tokens of the same config are out of scope.
 * Component and token names are kept exactly: src/theme/antdTheme.js merges
 * this object into ConfigProvider theme.components without remapping.
 * These stay separate from CU_LAUNCHER_TOKENS because the host scopes them to
 * components instead of declaring global tokens.
 */
export const CU_LAUNCHER_COMPONENT_OVERRIDES = Object.freeze({
	Card: Object.freeze({
		colorBorderSecondary: "#D9D9D9", // literal (host components.Card)
	}),
	Layout: Object.freeze({
		lightTriggerBg: "#E5E7EA", // literal (host components.Layout)
		bodyBg: "#F7F8F9", // N2 - Layout body exception vs global colorBgLayout
	}),
	Menu: Object.freeze({
		itemHoverBg: "#0000000F", // literal (host components.Menu)
	}),
	Badge: Object.freeze({
		colorBorderBg: "transparent", // literal (host components.Badge)
	}),
	Table: Object.freeze({
		headerBg: "#E5E7EA", // N4
		stickyScrollBarBg: "#D9D9D9", // literal
		borderColor: "#E5E7EA", // N4
		footerBg: "#FFFFFF", // N0
		headerSplitColor: "#C2C6CB", // N5
	}),
	Divider: Object.freeze({
		colorSplit: "#D9D9D9", // literal
	}),
	Tabs: Object.freeze({
		colorBorderSecondary: "#E5E7EA", // literal
	}),
	Breadcrumb: Object.freeze({
		linkColor: "#8D939C", // N7
	}),
	Input: Object.freeze({
		colorTextDisabled: "#9FA4AC", // N6 - input family exception vs colorTextDisabled
	}),
	InputNumber: Object.freeze({
		colorTextDisabled: "#9FA4AC", // N6 - input family exception vs colorTextDisabled
	}),
	Select: Object.freeze({
		colorTextDisabled: "#9FA4AC", // N6 - input family exception vs colorTextDisabled
	}),
	DatePicker: Object.freeze({
		colorTextDisabled: "#9FA4AC", // N6 - input family exception vs colorTextDisabled
	}),
});

/**
 * AntD seed status colors - NOT launcher COLORS tokens. The host COLORS object
 * declares no success/warning/error entries, so ConfigProvider inherits the
 * AntD defaults (the host runs antd ^6.6.0; this app runs antd 6.6.2).
 * ONLY the seeds are captured here. Derived hover/active values are resolved
 * by AntD's generation at runtime and are deliberately excluded - no parity
 * claim is made for them:
 * - reported antd 6 resolution: success hover #95DE64 (step 4), warning hover
 *   #FFD666, success active #389E0D, warning active #D48806
 * - error hover/active are UNKNOWN until runtime extraction
 * - the pre-wiring legacy builder theme declared the older ramp values
 *   success hover #73D13D / warning hover #FFC53D and error hover #FF7875 /
 *   active #D9363E; those declarations were removed at wiring - they were
 *   legacy builder values, not CU source values
 * Derived status states stay runtime-derived (the wired theme declares only
 * these seeds); do not treat any derived value as verified palette parity.
 */
export const CU_ANTD_SEED_STATUS = Object.freeze({
	colorSuccess: "#52C41A", // AntD seed default (not declared by host COLORS)
	colorWarning: "#FAAD14", // AntD seed default (not declared by host COLORS)
	colorError: "#FF4D4F", // AntD seed default (not declared by host COLORS)
});
